// User and Auth types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  // NULL for super-admin and patient roles.
  organizationId: string | null;
  // Set of hospitals this user is authorized against. Empty for super-admin and patient;
  // single entry for single-hospital roles; multi-entry for org admins and multi-hospital doctors.
  hospitalIds: string[];
  permissions: string[];
  createdAt: Date;
  updatedAt: Date;
}

export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  HOSPITAL_ADMIN = 'hospital_admin',
  DOCTOR = 'doctor',
  NURSE = 'nurse',
  RECEPTIONIST = 'receptionist',
  BILLING = 'billing',
  // Hospital-scoped warehouse/equipment/HR personas (CORA-242). Kept in sync
  // with the backend Role enum (apps/backend/src/domain/models/role.enum.ts).
  STOCK_MANAGER = 'stock_manager',
  // Organization-level: no hospital; works on the central site (CORA-409).
  CENTRAL_STOCK_MANAGER = 'central_stock_manager',
  BIOMEDICAL = 'biomedical',
  // Lab and radiology technicians, hospital-scoped (CORA-433).
  TECHNICIAN = 'technician',
  HR = 'hr',
  PATIENT = 'patient',
}

// CORA-409 — `X-Hospital-Id` value that selects the organization's central site (central warehouses) instead of a
// hospital or `ALL`. The API honours it only on routes allowed for the central site. Kept equal to the backend's
// CENTRAL_SITE_MARKER (guards/active-hospital.guard.ts) by dto/api-enum-parity.spec.ts.
export const CENTRAL_SITE_MARKER = 'CENTRAL';

// CORA-434 — the specialty of the pathologists who review and release lab results. Kept equal to the backend's
// PATHOLOGY_SPECIALTY_SLUG (domain/constants/default-specialty-catalog.ts) by dto/api-enum-parity.spec.ts.
export const PATHOLOGY_SPECIALTY_SLUG = 'pathology';

/**
 * Values the API accepts for `appointments.type`.
 *
 * The validating definition is `AppointmentType` in
 * `apps/backend/.../dto/appointments/appointment.dto.ts`; this copy exists so the shape
 * is describable from shared code and MUST be kept in step with it. These two drifted
 * once already — this enum carried `follow_up` (underscore) and a `checkup` value that
 * the API has never accepted, so anything written against it was rejected at the boundary.
 */
export enum AppointmentType {
  CONSULTATION = 'consultation',
  FOLLOW_UP = 'follow-up',
  EMERGENCY = 'emergency',
  PROCEDURE = 'procedure',
  TEST = 'test',
  VACCINATION = 'vaccination',
}

/**
 * Values the API accepts for `appointments.status`. Same contract as
 * {@link AppointmentType}: mirrors the DTO enum, which is what validation runs against.
 * The hyphens matter — this copy previously used `in_progress` / `no_show`.
 */
export enum AppointmentStatus {
  SCHEDULED = 'scheduled',
  CONFIRMED = 'confirmed',
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  NO_SHOW = 'no-show',
}

// CORA-359 — how a visit is paid for, on the patient and on each appointment booking. Kept in step with
// apps/backend/src/domain/constants/insurance-type.constants.ts by the backend's dto/api-enum-parity.spec.ts.
export const INSURANCE_TYPE_VALUES = ['particular', 'insurance', 'sns'] as const;

export type InsuranceType = (typeof INSURANCE_TYPE_VALUES)[number];

/**
 * Queue-entry status as the API stores and returns it (`QueueStatus` in
 * `apps/backend/.../domain/models/queue-entry.model.ts`, whitelisted for HTTP callers
 * by `QUEUE_STATUS_VALUES` in the queue DTO).
 *
 * This copy previously described a different model entirely — `waiting` / `called` /
 * `in_progress` / `no_show` — which no layer has ever used. The frontend maps these
 * values down to its own coarse `'waiting' | 'in-progress' | 'completed'` and to the
 * finer `workflowStage`; neither of those vocabularies belongs here.
 *
 * `AUTO_EXPIRED` is set only by the sweep cron, never by an HTTP caller.
 */
export enum QueueStatus {
  CHECKED_IN = 'CHECKED_IN',
  AWAITING_TRIAGE = 'AWAITING_TRIAGE',
  IN_TRIAGE = 'IN_TRIAGE',
  AWAITING_DOCTOR = 'AWAITING_DOCTOR',
  AWAITING_NURSE = 'AWAITING_NURSE',
  IN_CONSULTATION = 'IN_CONSULTATION',
  NOT_PRESENT = 'NOT_PRESENT',
  CHECKED_OUT = 'CHECKED_OUT',
  CANCELLED = 'CANCELLED',
  AUTO_EXPIRED = 'AUTO_EXPIRED',
}

// Inventory unit-of-measure vocabulary (CORA-262 follow-up). An inventory item's
// `unit` is a controlled slug, not free text; the frontend renders each slug via
// i18n (`warehouse.units.<slug>`). Kept in sync with the backend domain constant
// apps/backend/src/domain/constants/inventory-unit.constants.ts (mirrors the
// UserRole ↔ backend Role enum convention).
export const INVENTORY_UNITS = ['unit', 'box', 'pack', 'vial', 'ampoule', 'sachet', 'dose', 'tablet', 'ml', 'mg', 'g', 'l'] as const;

export type InventoryUnit = (typeof INVENTORY_UNITS)[number];

// Warehouse vocabulary (CORA-348). Kept in step with the backend domain constants
// apps/backend/src/domain/constants/warehouse.constants.ts: the backend's
// dto/api-enum-parity.spec.ts fails when the values or their order drift. The
// frontend renders each value via i18n (`warehouse.warehouses.types.<value>`,
// `warehouse.warehouses.activities.<value>`).
export enum WarehouseType {
  GENERAL = 'general',
  PHARMACY = 'pharmacy',
  COLD_CHAIN = 'cold_chain',
  CONSUMABLES = 'consumables',
  CONSIGNMENT = 'consignment',
}

export const WAREHOUSE_TYPE_VALUES: readonly WarehouseType[] = Object.values(WarehouseType);

/** Activities whose stock consumption is routed to a warehouse; an unmapped activity uses the hospital default. */
export enum WarehouseActivity {
  VACCINATION = 'vaccination',
  DISPENSING = 'dispensing',
  PROCEDURES = 'procedures',
  SURGERY = 'surgery',
}

// Also the order of the entries GET /warehouse-activity-mappings returns.
export const WAREHOUSE_ACTIVITY_VALUES: readonly WarehouseActivity[] = Object.values(WarehouseActivity);

export const WAREHOUSE_CODE_MAX_LENGTH = 20;
export const WAREHOUSE_NAME_MAX_LENGTH = 100;

// The API trims and upper-cases a code before checking it against this rule.
export const WAREHOUSE_CODE_PATTERN = new RegExp(`^[A-Z0-9][A-Z0-9_-]{0,${WAREHOUSE_CODE_MAX_LENGTH - 1}}$`);

// Stock transfer vocabulary (CORA-351; partially_received from CORA-353). Kept in step with
// apps/backend/src/domain/constants/stock-transfer.constants.ts by the backend's
// dto/api-enum-parity.spec.ts. The frontend renders each status via i18n.
export enum StockTransferStatus {
  DRAFT = 'draft',
  IN_TRANSIT = 'in_transit',
  PARTIALLY_RECEIVED = 'partially_received',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export const STOCK_TRANSFER_STATUS_VALUES: readonly StockTransferStatus[] = Object.values(StockTransferStatus);

// What the caller may do with a transfer now: each response lists them in `allowedActions` (CORA-413). Kept in step
// with the backend's StockTransferAction by dto/api-enum-parity.spec.ts.
export enum StockTransferAction {
  EDIT = 'edit',
  DISPATCH = 'dispatch',
  RECEIVE = 'receive',
  RETURN_REMAINDER = 'return_remainder',
  CANCEL = 'cancel',
}

export const STOCK_TRANSFER_ACTION_VALUES: readonly StockTransferAction[] = Object.values(StockTransferAction);

// The transfer list's filter by the caller's side (CORA-421): GET /stock-transfers?direction=. Kept in step with the
// backend's StockTransferDirection by dto/api-enum-parity.spec.ts.
export enum StockTransferDirection {
  INCOMING = 'incoming',
  OUTGOING = 'outgoing',
}

export const STOCK_TRANSFER_DIRECTION_VALUES: readonly StockTransferDirection[] = Object.values(StockTransferDirection);

export const STOCK_TRANSFER_MAX_LINES = 100;
export const STOCK_TRANSFER_NOTES_MAX_LENGTH = 1000;

// Stock request vocabulary (CORA-415). Kept in step with apps/backend/src/domain/constants/stock-request.constants.ts
// by the backend's dto/api-enum-parity.spec.ts. Each request response lists what the caller may do now in
// `allowedActions`; the frontend renders each value via i18n.
export enum StockRequestStatus {
  REQUESTED = 'requested',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  CANCELLED = 'cancelled',
}

export const STOCK_REQUEST_STATUS_VALUES: readonly StockRequestStatus[] = Object.values(StockRequestStatus);

export enum StockRequestAction {
  CANCEL = 'cancel',
  APPROVE = 'approve',
  REJECT = 'reject',
  PREPARE_SHIPMENT = 'prepare_shipment',
}

export const STOCK_REQUEST_ACTION_VALUES: readonly StockRequestAction[] = Object.values(StockRequestAction);

export const STOCK_REQUEST_NUMBER_PREFIX = 'REQ-';
export const STOCK_REQUEST_MAX_LINES = 100;
// The notes, the decision note and the cancellation reason.
export const STOCK_REQUEST_NOTES_MAX_LENGTH = 1000;
export const STOCK_REQUEST_ITEM_CODE_MAX_LENGTH = 50;

// CORA-190 — DOB sentinel written for ER provisional patients, whose intake
// captures only name + vitals while `patients.date_of_birth` is NOT NULL.
// Mirrors PROVISIONAL_PATIENT_DEFAULTS in the backend queue repository. UI must
// treat a DOB equal to this as "unknown" and render the em-dash placeholder —
// computing an age from it yields a nonsense figure (e.g. "126 years").
export const PROVISIONAL_DATE_OF_BIRTH = '1900-01-01';

// A patient's first and last name; kept equal to the backend's PATIENT_NAME_MAX_LENGTH by dto/api-enum-parity.spec.ts.
export const PATIENT_NAME_MAX_LENGTH = 100;

// CORA-358 — preparation instructions on the exam-type and treatment catalogs. Kept equal to the backend domain
// constant in apps/backend/src/domain/constants/preparation-instructions.constants.ts by dto/api-enum-parity.spec.ts.
export const PREPARATION_INSTRUCTIONS_MAX_LENGTH = 2000;

// CORA-375 — surgery types catalog limits. Kept equal to apps/backend/src/domain/constants/surgery-type.constants.ts
// by dto/api-enum-parity.spec.ts.
export const SURGERY_TYPE_NAME_MAX_LENGTH = 120;
export const SURGERY_TYPE_DESCRIPTION_MAX_LENGTH = 500;
export const SURGERY_TYPE_MIN_DURATION_MINUTES = 5;
export const SURGERY_TYPE_MAX_DURATION_MINUTES = 1440;

// CORA-376 — operating rooms. Kept equal to apps/backend/src/domain/constants/operating-room.constants.ts by
// dto/api-enum-parity.spec.ts. Hours are HH:mm on the hospital's clock.
export enum OperatingRoomStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export const OPERATING_ROOM_NAME_MAX_LENGTH = 100;
export const OPERATING_ROOM_NOTES_MAX_LENGTH = 1000;
export const MAINTENANCE_WINDOW_REASON_MAX_LENGTH = 500;
export const OPERATING_ROOM_DEFAULT_OPENING_TIME = '08:00';
export const OPERATING_ROOM_DEFAULT_CLOSING_TIME = '18:00';

// CORA-378 — surgery scheduling.
export enum SurgeryStatus {
  SCHEDULED = 'scheduled',
  IN_PROGRESS = 'in_progress',
  RECOVERY = 'recovery',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum SurgeryTeamMemberType {
  DOCTOR = 'doctor',
  NURSE = 'nurse',
}

export enum SurgeryTeamRole {
  SURGEON = 'surgeon',
  ASSISTANT_SURGEON = 'assistant_surgeon',
  ANESTHESIOLOGIST = 'anesthesiologist',
  SCRUB_NURSE = 'scrub_nurse',
  CIRCULATING_NURSE = 'circulating_nurse',
}

export const SURGERY_DOCTOR_TEAM_ROLES: readonly SurgeryTeamRole[] = [
  SurgeryTeamRole.SURGEON,
  SurgeryTeamRole.ASSISTANT_SURGEON,
  SurgeryTeamRole.ANESTHESIOLOGIST,
];

export const SURGERY_NURSE_TEAM_ROLES: readonly SurgeryTeamRole[] = [SurgeryTeamRole.SCRUB_NURSE, SurgeryTeamRole.CIRCULATING_NURSE];

export const SURGERY_TEAM_MAX_MEMBERS = 20;

/** The team rule a refused booking names (SURGERY_TEAM_INVALID's `errors.rule`). */
export enum SurgeryTeamRule {
  ROLE_FITS_MEMBER_TYPE = 'role_fits_member_type',
  ONE_SURGEON = 'one_surgeon',
  AT_MOST_ONE_ANESTHESIOLOGIST = 'at_most_one_anesthesiologist',
  MEMBER_ONCE = 'member_once',
}

export enum SurgeryCancellationReason {
  PATIENT_REQUEST = 'patient_request',
  PATIENT_NO_SHOW = 'patient_no_show',
  PATIENT_UNFIT = 'patient_unfit',
  SURGEON_UNAVAILABLE = 'surgeon_unavailable',
  ROOM_UNAVAILABLE = 'room_unavailable',
  SCHEDULING_ERROR = 'scheduling_error',
  OTHER = 'other',
}

export const SURGERY_CANCELLATION_NOTE_MAX_LENGTH = 1000;

export enum SurgeryConflictKind {
  ROOM_SURGERY = 'room_surgery',
  MAINTENANCE_WINDOW = 'maintenance_window',
  SURGEON_SURGERY = 'surgeon_surgery',
}

export const ANESTHESIOLOGY_SPECIALTY_SLUG = 'anesthesiology';

// CORA-381 — surgery checklist templates: one engine for the pre-op checklist and the discharge criteria.
export enum SurgeryChecklistPhase {
  PRE_OP = 'pre_op',
  DISCHARGE = 'discharge',
}

export enum SurgeryChecklistAppliesTo {
  AMBULATORY = 'ambulatory',
  NON_AMBULATORY = 'non_ambulatory',
  BOTH = 'both',
}

export const SURGERY_CHECKLIST_TEMPLATE_NAME_MAX_LENGTH = 120;
export const SURGERY_CHECKLIST_ITEM_LABEL_MAX_LENGTH = 300;
export const SURGERY_CHECKLIST_TEMPLATE_MAX_ITEMS = 50;

// CORA-382 — pre-operative clearance: the anesthesia plan, its sign-off, the lab review and the consent.
export enum AnesthesiaType {
  GENERAL = 'general',
  SPINAL = 'spinal',
  EPIDURAL = 'epidural',
  REGIONAL_BLOCK = 'regional_block',
  SEDATION = 'sedation',
  LOCAL = 'local',
}

export const ANESTHESIA_AGENTS_MAX_LENGTH = 500;

/** What still keeps a surgery from starting (the clearance's `missing`, SURGERY_NOT_CLEARED's `errors.missing`). */
export enum SurgeryClearanceMissing {
  PRE_OP_ITEMS = 'pre_op_items',
  ANESTHESIA_SIGN_OFF = 'anesthesia_sign_off',
  LAB_REVIEW = 'lab_review',
  CONSENT = 'consent',
}

export enum SurgeryConsentStatus {
  SIGNED = 'signed',
  NOT_SIGNED = 'not_signed',
}

/** Why the caller may not give an anesthesia or lab-review clearance (the 403's `errors.reason`). */
export enum SurgeryClearanceRefusal {
  NOT_A_DOCTOR = 'not_a_doctor',
  NOT_ANESTHESIOLOGY = 'not_anesthesiology',
  NOT_AT_HOSPITAL = 'not_at_hospital',
  NOT_ON_TEAM = 'not_on_team',
  MEMBER_INACTIVE = 'member_inactive',
}

// CORA-385 — intra-operative record: procedure notes and complications with a Clavien–Dindo grade.
export enum ClavienDindoGrade {
  I = 'I',
  II = 'II',
  IIIA = 'IIIa',
  IIIB = 'IIIb',
  IVA = 'IVa',
  IVB = 'IVb',
  V = 'V',
}

export const SURGERY_INCISION_MAX_LENGTH = 500;
export const SURGERY_FINDINGS_MAX_LENGTH = 4000;
export const SURGERY_TECHNIQUE_MAX_LENGTH = 4000;
export const SURGERY_COMPLICATION_TEXT_MAX_LENGTH = 2000;

export enum SurgeryComplicationRule {
  GRADE_REQUIRED = 'grade_required',
  GRADE_WITHOUT_COMPLICATIONS = 'grade_without_complications',
  TEXT_WITHOUT_COMPLICATIONS = 'text_without_complications',
}

export enum SurgeryActionRefusal {
  ROLE_NOT_ALLOWED = 'role_not_allowed',
  NOT_ON_TEAM = 'not_on_team',
  NO_PROFILE = 'no_profile',
  MEMBER_INACTIVE = 'member_inactive',
}

export const SURGERY_INTRA_OP_EDITABLE_STATUSES: readonly SurgeryStatus[] = [SurgeryStatus.IN_PROGRESS, SurgeryStatus.RECOVERY];

// CORA-395 — surgeon performance report.
export enum ClavienDindoReportGrade {
  I = 'I',
  II = 'II',
  III = 'III',
  IV = 'IV',
  V = 'V',
}

// CORA-395/396 — surgery reports.
export const REPORT_MAX_WINDOW_DAYS = 366;

// CORA-229 — why a consumption line moved no stock, or moved a rounded quantity (a line's `warningCode`), and
// reversal_failed for a void whose stock could not be returned.
export enum StockConsumptionWarningCode {
  NOT_TRACKED = 'not_tracked',
  INSUFFICIENT = 'insufficient',
  NO_ACTIVE_HOSPITAL = 'no_active_hospital',
  ROUNDED = 'rounded',
  DECREMENT_FAILED = 'decrement_failed',
  EXPIRED_LOT = 'expired_lot',
  REVERSAL_FAILED = 'reversal_failed',
}

// CORA-386 — surgery materials and implants consumed from the surgery warehouse.
export enum SurgeryMaterialStatus {
  CONFIRMED = 'confirmed',
  VOIDED = 'voided',
}

export enum SurgeryMaterialVoidReason {
  ENTERED_IN_ERROR = 'entered_in_error',
  WRONG_ITEM = 'wrong_item',
  WRONG_QUANTITY = 'wrong_quantity',
  NOT_USED = 'not_used',
  OTHER = 'other',
}

export enum SurgeryMaterialLineRule {
  IMPLANT_FIELDS_WITHOUT_IMPLANT = 'implant_fields_without_implant',
  IMPLANT_TEXT_TOO_LONG = 'implant_text_too_long',
  QUANTITY_BELOW_ONE_UNIT = 'quantity_below_one_unit',
  QUANTITY_ABOVE_MAXIMUM = 'quantity_above_maximum',
}

export const SURGERY_MATERIAL_MAX_LINES = 100;
export const SURGERY_MATERIAL_LOT_TEXT_MAX_LENGTH = 100;
export const SURGERY_MATERIAL_SERIAL_TEXT_MAX_LENGTH = 100;
export const SURGERY_MATERIAL_UDI_TEXT_MAX_LENGTH = 255;
export const SURGERY_MATERIAL_VOID_NOTE_MAX_LENGTH = 1000;
export const CONSUMPTION_LINE_MAX_QUANTITY = 10000;

// CORA-393 — standard kits per surgery type, applied to a surgery's materials before the confirm.
export enum SurgeryKitPhase {
  PRE_OP = 'pre_op',
  POST_OP = 'post_op',
}

export const SURGERY_KIT_NAME_MAX_LENGTH = 120;
export const SURGERY_KIT_MAX_ITEMS = 100;

// CORA-388 — recovery record.
export const SURGERY_RECOVERY_NOTES_MAX_LENGTH = 4000;

// CORA-394 — the post-op follow-up booked at discharge, configured per surgery type and hospital.
export const SURGERY_FOLLOW_UP_MIN_OFFSET_DAYS = 0;
export const SURGERY_FOLLOW_UP_MAX_OFFSET_DAYS = 365;
export const SURGERY_FOLLOW_UP_SEARCH_WINDOW_DAYS = 30;

export enum SurgeryFollowUpStatus {
  BOOKED = 'booked',
  MANUAL = 'manual',
  NOT_CONFIGURED = 'not_configured',
}

export enum SurgeryFollowUpManualReason {
  SURGEON_UNAVAILABLE = 'surgeon_unavailable',
  NO_SCHEDULE = 'no_schedule',
  NO_FREE_SLOT = 'no_free_slot',
  BOOKING_REFUSED = 'booking_refused',
  BOOKING_FAILED = 'booking_failed',
}

// What the vital-signs and triage APIs take for a reading; they answer 400 outside these bounds.
export const VITAL_SIGNS_TEMPERATURE_MIN_CELSIUS = 10;
export const VITAL_SIGNS_TEMPERATURE_MAX_CELSIUS = 50;
export const VITAL_SIGNS_SYSTOLIC_MAX = 300;
export const VITAL_SIGNS_DIASTOLIC_MAX = 200;
export const VITAL_SIGNS_HEART_RATE_MAX = 300;
export const VITAL_SIGNS_RESPIRATORY_RATE_MAX = 100;
export const VITAL_SIGNS_OXYGEN_SATURATION_MAX = 100;
export const VITAL_SIGNS_PAIN_SCALE_MAX = 10;
/** Height and the circumferences, in cm. */
export const VITAL_SIGNS_LENGTH_CM_MAX = 999.99;
/** Weight in kg, and blood glucose. */
export const VITAL_SIGNS_WEIGHT_GLUCOSE_MAX = 9999.99;
export const VITAL_SIGNS_PAIN_LOCATION_MAX_LENGTH = 200;
export const RAPID_TRIAGE_NAME_MAX_LENGTH = 100;

// CORA-389 — post-operative note: condition, complications, expected recovery, diet restriction and instructions.
export enum SurgeryDietRestriction {
  NIL_BY_MOUTH = 'nil_by_mouth',
  CLEAR_FLUIDS = 'clear_fluids',
  SOFT = 'soft',
  NORMAL = 'normal',
  DIABETIC = 'diabetic',
  OTHER = 'other',
}

export const SURGERY_POST_OP_CONDITION_MAX_LENGTH = 2000;
export const SURGERY_POST_OP_COMPLICATIONS_MAX_LENGTH = 2000;
export const SURGERY_POST_OP_EXPECTED_RECOVERY_MAX_LENGTH = 1000;
export const SURGERY_POST_OP_INSTRUCTIONS_MAX_LENGTH = 2000;
